import { observable, action, computed } from 'mobx';
import DocumentViewService from '../services/DocumentViewService';

type FileType = 'pdf' | 'text' | 'excel' | 'image';

export class DocumentViewVisualStore {
    @observable
    pageWidth: number | null = null;

    @observable
    scale: number = 1;

    @observable
    fileBlob: Blob | null = null;

    @observable
    fileName: string = '';

    @observable
    isLoading: boolean = false;

    @computed
    get file(): File | null {
        if (!this.fileBlob) {
            return null;
        }

        return new File([this.fileBlob], this.fileName, { type: this.fileBlob.type });
    }

    @computed
    get fileType(): FileType | null {
        if (!this.file) {
            return null;
        }

        if (this.file.type.startsWith('image/')) {
            return 'image';
        }

        switch (this.file.type) {
            case 'application/pdf':
                return 'pdf';
            case 'text/plain':
                return 'text';
            case 'application/vnd.ms-excel.sheet.macroenabled.12':
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return 'excel';
            default:
                return this.hasImageFileName ? 'image' : null;
        }
    }

    private get hasImageFileName(): boolean {
        return /\.(apng|avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(this.fileName);
    }

    constructor(private readonly service: DocumentViewService) {}

    @action
    setPageWidth(width: number) {
        if (width === this.pageWidth) {
            return;
        }

        this.pageWidth = width;
    }

    @action
    setScale(scale: number) {
        this.scale = scale / 100;
    }

    @action
    async getDocument(path: string, fileName?: string) {
        this.isLoading = true;

        try {
            this.fileName = fileName || 'N/A';

            // Use preview variant (sanitized for Excel) for viewer context.
            const fileBlob = await this.service.getPreviewDocument(path);

            if (!fileBlob) {
                return;
            }

            this.fileBlob = fileBlob;
        } finally {
            this.isLoading = false;
        }
    }
}
export default DocumentViewVisualStore;
